/* eslint-disable no-undef */
import { isRoomValid } from '../base/conference/functions';
import { setRoom } from '../base/conference/actions';
import {
  configWillLoad,
  loadConfigError,
  setConfig,
  storeConfig
} from '../base/config/actions';
import { createFakeConfig, restoreConfig } from '../base/config/functions';
import { disconnect, setLocationURL } from '../base/connection/actions';
import { loadConfig } from '../base/lib-jitsi-meet/functions';
import { toState } from '../base/redux/functions';
import { inIframe } from '../base/util/iframeUtils';
import { parseURLParams } from '../base/util/parseURLParams';
import {
  appendURLParam,
  getBackendSafeRoomName,
  parseURIString
} from '../base/util/uri';
import { isVpaasMeeting } from '../jaas/functions';
import { clearNotifications, showNotification } from '../notifications/actions';
import { NOTIFICATION_TIMEOUT_TYPE } from '../notifications/constants';
import { setFatalError } from '../overlay/actions';
import {
  addTrackStateToURL,
  fetchUserConfig,
  getDefaultURL,
  getName
} from './functions';
import { getLocationContextRoot } from '../base/util/uri';
import { API_ID } from 'util/API';
import { history } from 'CustomHistory';
import { updateSettings } from '../base/settings/actions';
import {
  joinRotation,
  leaveRotation,
  setUserDetails,
  updateDevice
} from '../login/actions';
import { updateSleepSettings } from '../settings/actions';
import utils from 'util/utils';
import logger from './logger';

/**
 * Redirects to another page generated by replacing the path in the original URL
 * with the given path.
 *
 * @param {(string)} pathname - The path to navigate to.
 * @returns {Function}
 */
export function redirectWithStoredParams(pathname) {
  return (dispatch, getState) => {
    const { locationURL } = getState()['features/base/connection'];
    const newLocationURL = new URL(locationURL?.href ?? '');

    newLocationURL.pathname = pathname;
    window.location.assign(newLocationURL.toString());
  };
}

/**
 * Assigns a specific pathname to window.location.pathname taking into account
 * the context root of the Web app.
 *
 * @param {string} pathname - The pathname to assign to
 * window.location.pathname. If the specified pathname is relative, the context
 * root of the Web app will be prepended to the specified pathname before
 * assigning it to window.location.pathname.
 * @param {string} hashParam - Optional hash param to assign to
 * window.location.hash.
 * @returns {Function}
 */
export function redirectToStaticPage(pathname, hashParam) {
  return () => {
    const windowLocation = window.location;
    let newPathname = pathname;

    if (!newPathname.startsWith('/')) {
      // A pathname equal to ./ specifies the current directory. It will be
      // fine but pointless to include it because contextRoot is the current
      // directory.
      newPathname.startsWith('./') && (newPathname = newPathname.substring(2));
      newPathname = getLocationContextRoot(windowLocation) + newPathname;
    }

    if (hashParam) {
      windowLocation.hash = hashParam;
    }

    windowLocation.pathname = newPathname;
  };
}

/**
 * Reloads the page by restoring the original URL.
 *
 * @returns {Function}
 */
export function reloadWithStoredParams() {
  return (dispatch, getState) => {
    const state = getState();
    const { room } = state['features/base/settings'];
    const defaultLocation = getDefaultURL(getState);
    // Preserve the local tracks muted states.
    const newURL = addTrackStateToURL(defaultLocation + '/' + room, state);
    window.meetingUrl = newURL.toString();
    logger.info(`Reloading the conference with params: ${newURL}`);
    dispatch(appNavigate(newURL.toString()));

    // windowLocation.replace(newURL.toString());
    // const oldSearchString = windowLocation.search;

    // if (newURL.search === oldSearchString) {
    // NOTE: Assuming that only the hash or search part of the URL will
    // be changed!
    // location.replace will not trigger redirect/reload when
    // only the hash params are changed. That's why we need to call
    // reload in addition to replace.
    // windowLocation.reload();
    // }
  };
}

/**
 * Triggers an in-app navigation to a specific route. Allows navigation to be
 * abstracted between the mobile/React Native and Web/React applications.
 *
 * @param {string|undefined} uri - The URI to which to navigate. It may be a
 * full URL with an HTTP(S) scheme, a full or partial URI with the app-specific
 * scheme, or a mere room name.
 * @returns {Function}
 */
export function appNavigate(uri, isGroupChange = false) {
  return async (dispatch, getState) => {
    let location = parseURIString(uri);
    // If the specified location (URI) does not identify a host, use the app's
    // default.
    if (!location || !location.host) {
      const defaultLocation = parseURIString(getDefaultURL(getState));

      if (location) {
        location.host = defaultLocation.host;

        // FIXME Turn location's host, hostname, and port properties into
        // setters in order to reduce the risks of inconsistent state.
        location.hostname = defaultLocation.hostname;
        location.pathname = '/' + location.pathname.substr(1);
        location.port = defaultLocation.port;
        location.protocol = defaultLocation.protocol;
      } else {
        location = defaultLocation;
      }
    }
    location.protocol || (location.protocol = 'https:');
    const { contextRoot, host, room } = location;
    // console.log(location);
    if (room) {
      const state = toState(getState);
      const { user } = state['features/login'];

      const locationURL = new URL(location.toString());

      // There are notifications now that gets displayed after we technically left
      // the conference, but we're still on the conference screen.
      dispatch(clearNotifications());

      dispatch(configWillLoad(locationURL, room));

      let protocol = location.protocol.toLowerCase();

      // The React Native app supports an app-specific scheme which is sure to not
      // be supported by fetch.
      protocol !== 'http:' && protocol !== 'https:' && (protocol = 'https:');

      const baseURL = `${protocol}//${host}${contextRoot || '/'}`;
      let url = `${baseURL}config.js`;

      // XXX In order to support multiple shards, tell the room to the deployment.
      room &&
        (url = appendURLParam(url, 'room', getBackendSafeRoomName(room) ?? ''));

      const { release } = parseURLParams(location, true, 'search');

      release && (url = appendURLParam(url, 'release', release));

      let config;

      // Avoid (re)loading the config when there is no room.
      if (!room) {
        config = restoreConfig(baseURL);
      }

      if (!config) {
        try {
          config = await loadConfig(url);
          dispatch(storeConfig(baseURL, config));
        } catch (error) {
          config = restoreConfig(baseURL);

          if (!config) {
            if (room) {
              dispatch(loadConfigError(error, locationURL));

              return;
            }

            // If there is no room (we are on the welcome page), don't fail, just create a fake one.
            logger.warn(
              'Failed to load config but there is no room, applying a fake one'
            );
            config = createFakeConfig(baseURL);
          }
        }
      }

      if (getState()['features/base/config'].locationURL !== locationURL) {
        dispatch(
          loadConfigError(new Error('Config no longer needed!'), locationURL)
        );

        return;
      }
      dispatch(setLocationURL(locationURL));
      config = fetchUserConfig(config, user);
      dispatch(setConfig(config));
      dispatch(setRoom(room));
      dispatch(updateSettings({ room }));
      dispatch(
        updateSleepSettings({
          userExternalMode: false,
          userPrivacyMode: false,
          userSleepMode: false
        })
      );
      if (isRoomValid(room)) {
        if (isGroupChange) {
          dispatch(joinRotation(room));
        }
        history.replace('/conference', { room });
      } else {
        if (user) history.push('/settings');
        else history.replace('/login');
      }
    }
  };
}

export function updateDeviceStatus(status) {
  return (dispatch, getState) => {
    const state = toState(getState);
    const { user } = state['features/login'];
    if (user?.device) {
      const statuses = user.device.status ? [...user.device.status] : [];
      const data = {
        device: user.device._id,
        status,
        identifier: utils.fetchLocalUUID()
      };
      let added = false;
      statuses.forEach((item) => {
        if (item.identifier === data.identifier) {
          item.status = status;
          added = true;
        }
      });
      if (!added) statuses.push({ identifier: data.identifier, status });

      dispatch(
        setUserDetails({
          ...user,
          device: { ...user.device, status: statuses }
        })
      ).then(() => {
        dispatch(updateDevice(data));
      });
    }
  };
}
export function loadInitialConfig() {
  return async (dispatch, getState) => {
    const baseURL = getDefaultURL(getState);
    const locationURL = new URL(baseURL);
    dispatch(clearNotifications());

    dispatch(configWillLoad(locationURL, null));
    let url = `${baseURL}config.js`;
    let config = restoreConfig();

    if (!config) {
      try {
        config = await loadConfig(url);
        dispatch(storeConfig(baseURL, config));
      } catch (error) {
        config = restoreConfig(baseURL);

        if (!config) {
          // If there is no room (we are on the welcome page), don't fail, just create a fake one.
          logger.warn(
            'Failed to load config but there is no room, applying a fake one'
          );
          config = createFakeConfig(baseURL);
        }
      }
    }

    dispatch(setLocationURL(locationURL));
    dispatch(setConfig(config));
  };
}

/**
 * Check if the welcome page is enabled and redirects to it.
 * If requested show a thank you dialog before that.
 * If we have a close page enabled, redirect to it without
 * showing any other dialog.
 *
 * @param {Object} options - Used to decide which particular close page to show
 * or if close page is disabled, whether we should show the thankyou dialog.
 * @param {boolean} options.showThankYou - Whether we should
 * show thank you dialog.
 * @param {boolean} options.feedbackSubmitted - Whether feedback was submitted.
 * @returns {Function}
 */
export function maybeRedirectToWelcomePage(options = {}) {
  return (dispatch, getState) => {
    const { enableClosePage } = getState()['features/base/config'];

    // if close page is enabled redirect to it, without further action
    if (enableClosePage) {
      if (isVpaasMeeting(getState())) {
        const isOpenedInIframe = inIframe();

        if (isOpenedInIframe) {
          window.location = 'about:blank';
        } else {
          dispatch(redirectToStaticPage('/'));
        }

        return;
      }

      const { jwt } = getState()['features/base/jwt'];

      let hashParam;

      // save whether current user is guest or not, and pass auth token,
      // before navigating to close page
      window.sessionStorage.setItem('guest', (!jwt).toString());
      window.sessionStorage.setItem('jwt', jwt ?? '');

      let path = 'close.html';

      if (interfaceConfig.SHOW_PROMOTIONAL_CLOSE_PAGE) {
        if (Number(API_ID) === API_ID) {
          hashParam = `#jitsi_meet_external_api_id=${API_ID}`;
        }
        path = 'close3.html';
      } else if (!options.feedbackSubmitted) {
        path = 'close2.html';
      }

      dispatch(redirectToStaticPage(`static/${path}`, hashParam));

      return;
    }

    // else: show thankYou dialog only if there is no feedback
    if (options.showThankYou) {
      dispatch(
        showNotification(
          {
            titleArguments: { appName: getName() },
            titleKey: 'dialog.thankYou'
          },
          NOTIFICATION_TIMEOUT_TYPE.STICKY
        )
      );
    }

    // if Welcome page is enabled redirect to welcome page after 3 sec, if
    // there is a thank you message to be shown, 0.5s otherwise.
    // if (getState()['features/base/config'].enableWelcomePage) {
    //     setTimeout(
    //         () => {
    //             dispatch(redirectWithStoredParams('/'));
    //         },
    //         options.showThankYou ? 3000 : 500);
    // }
  };
}

/**
 * Reloads the page.
 *
 * @protected
 * @returns {Function}
 */
export function reloadNow() {
  return (dispatch, getState) => {
    dispatch(setFatalError(undefined));
    dispatch(leaveConference());
    // TODO Manual Reload
    // dispatch(reloadWithStoredParams());
    const { room } = getState()['features/base/settings'];
    setTimeout(() => {
      logger.info(`Reloading the conference using URL: ${room}`);
      history.replace('/groups', {
        room,
        groupChange: true
      });
    }, 500);
  };
}

/**
 * Action to leave a conference.
 *
 * @returns {Function}
 */
export function leaveConference(route, state) {
  return (dispatch) => {
    dispatch(leaveRotation());
    dispatch(setRoom(undefined));
    // TODO
    dispatch(disconnect(true));
    if (route) {
      // window.location = route;
      // window.location.reload();
      setTimeout(() => {
        history.replace(route, state);
      }, 1000);
    }
  };
}
